import { useMemo, useState } from 'react';
import { compilations } from '@/mocks/home';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'new', label: '最新' },
  { key: 'hot', label: '最热' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

export default function CompilationGrid() {
  const [active, setActive] = useState<TabKey>('all');

  const list = useMemo(() => {
    const arr = [...compilations];
    if (active === 'new') {
      arr.sort((a, b) => b.date.localeCompare(a.date));
    } else if (active === 'hot') {
      arr.sort((a, b) => b.comments - a.comments);
    }
    return arr;
  }, [active]);

  return (
    <section id="compilation" className="border-b border-background-200/70 bg-background-100">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <SectionHeading
          kicker="黑料合集"
          title="大图卡片流：一屏看完整套黑料合集"
          desc="每条合集都按题材打包，封面直接对上内容，点进去就是时间线、出处与二次回应的完整整理。"
          accent="primary"
          action={{ label: '全部合集', href: '#compilation' }}
        />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-primary-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-foreground-500">
            共 {compilations.length} 个合集 · 按题材归档
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {list.map((item, index) => (
            <Reveal key={item.id} delay={index * 60}>
              <a
                href="#compilation"
                className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
              >
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.alt}
                    title={item.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-primary-500/95 px-2 py-1 text-xs text-background-50">
                    {item.category}
                  </span>
                  <span className="absolute right-3 top-3 rounded-md bg-black/45 px-2 py-1 text-xs text-background-100 backdrop-blur-sm">
                    {item.date}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.summary}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-accent-100 px-2 py-0.5 text-xs text-accent-900"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-chat-3-line"></i>
                      {item.comments}
                    </span>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}